import React, { useState, useEffect, useMemo } from "react";
import Calendar from "react-calendar";
import api from "../utils/axiosConfig";
import SideBar from "./SideBar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
  faCalendarAlt,
  faMapMarkerAlt,
  faStar,
  faBuilding,
  faUmbrellaBeach,
} from "@fortawesome/free-solid-svg-icons";
import "react-calendar/dist/Calendar.css";
import "../styles/holiday-calendar.css";

const API_URL = process.env.REACT_APP_API_URL;

const HolidayCalendar = () => {
  const [holidays, setHolidays] = useState([]);
  const [filteredHolidays, setFilteredHolidays] = useState([]);

  // ✅ Keep selected day and visible month separate
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [activeStartDate, setActiveStartDate] = useState(
    new Date(new Date().getFullYear(), new Date().getMonth(), 1),
  );

  // ✅ Location filter removed, so always show "All Locations"
  const [loading, setLoading] = useState(true);
  const [selectedHoliday, setSelectedHoliday] = useState(null);

  // ✅ backend date is "DD-MM-YYYY"
  const parseDDMMYYYY = (dateStr) => {
    if (!dateStr) return null;
    const [dd, mm, yyyy] = String(dateStr).split("-").map(Number);
    if (!dd || !mm || !yyyy) return null;
    return new Date(yyyy, mm - 1, dd); // local date
  };

  const normalize = (d) => {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
  };

  const toKey = (d) => {
    const x = normalize(d);
    const yyyy = x.getFullYear();
    const mm = String(x.getMonth() + 1).padStart(2, "0");
    const dd = String(x.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  // ✅ Build fast lookup map for holidays
  const holidayMap = useMemo(() => {
    const map = new Map();
    for (const h of filteredHolidays) {
      const d = parseDDMMYYYY(h.date);
      if (!d) continue;
      map.set(toKey(d), h);
    }
    return map;
  }, [filteredHolidays]);

  const getHolidayForDate = (d) => holidayMap.get(toKey(d)) || null;

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const response = await api.get(`${API_URL}/holiday/holidays`);
        const all = response.data || [];

        setHolidays(all);

        // ✅ NO FILTER — show all holidays
        setFilteredHolidays(all);

        // ✅ preselect today's holiday if exists
        const today = new Date();
        const todayHoliday = all.find((h) => {
          const hd = parseDDMMYYYY(h.date);
          return hd && toKey(hd) === toKey(today);
        });
        setSelectedHoliday(todayHoliday || null);
      } catch (error) {
        console.error("Error fetching holidays:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const tileClassName = ({ date, view }) => {
    if (view !== "month") return undefined;

    const holiday = getHolidayForDate(date);
    if (holiday) {
      const type = (holiday.holidayType || "").toLowerCase();
      if (type.includes("national")) return "holiday-tile national-holiday";
      if (type.includes("restricted")) return "holiday-tile restricted-holiday";
      if (type.includes("fixed")) return "holiday-tile fixed-holiday";
      return "holiday-tile";
    }

    // Sunday highlight
    if (date.getDay() === 0) return "sunday-tile";

    return undefined; // keep default styles for normal tiles
  };

  const tileContent = ({ date, view }) => {
    if (view !== "month") return null;

    const holiday = getHolidayForDate(date);
    if (!holiday) return null;

    return <div className="holiday-marker-dot" title={holiday.holiday} />;
  };

  const handleDateClick = (value) => {
    setSelectedDate(value);
    setSelectedHoliday(getHolidayForDate(value));
    setActiveStartDate(new Date(value.getFullYear(), value.getMonth(), 1));
  };

  const goToPrevMonth = () => {
    const prev = new Date(
      activeStartDate.getFullYear(),
      activeStartDate.getMonth() - 1,
      1,
    );
    setActiveStartDate(prev);
    setSelectedDate(prev);
    setSelectedHoliday(getHolidayForDate(prev));
  };

  const goToNextMonth = () => {
    const next = new Date(
      activeStartDate.getFullYear(),
      activeStartDate.getMonth() + 1,
      1,
    );
    setActiveStartDate(next);
    setSelectedDate(next);
    setSelectedHoliday(getHolidayForDate(next));
  };

  const goToToday = () => {
    const today = new Date();
    setSelectedDate(today);
    setActiveStartDate(new Date(today.getFullYear(), today.getMonth(), 1));
    setSelectedHoliday(getHolidayForDate(today));
  };

  const getHolidayIcon = (type) => {
    const t = (type || "").toLowerCase();
    if (t.includes("national")) return faStar;
    if (t.includes("restricted")) return faBuilding;
    return faUmbrellaBeach;
  };

  const getUpcomingHolidays = () => {
    const today = normalize(new Date());

    return filteredHolidays
      .map((h) => ({ ...h, _d: parseDDMMYYYY(h.date) }))
      .filter((h) => h._d && normalize(h._d) >= today)
      .sort((a, b) => normalize(a._d) - normalize(b._d))
      .slice(0, 5);
  };

  return (
    <>
      <SideBar />
      <div className="dashboard-container">
        {/* Header */}
        <div className="dashboard-header">
          <div className="d-flex align-items-center gap-3">
            <div className="calendar-header-icon">
              <FontAwesomeIcon icon={faCalendarAlt} />
            </div>
            <div>
              <h1 className="dashboard-title">Calendar</h1>
              <p className="dashboard-subtitle">
                <FontAwesomeIcon
                  icon={faMapMarkerAlt}
                  style={{ marginRight: "8px" }}
                />
                All Locations
              </p>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading holidays...</p>
          </div>
        ) : (
          <div className="calendar-layout">
            {/* Main Calendar */}
            <div className="calendar-main-section">
              <div className="card calendar-card">
                {/* Navigation */}
                <div className="calendar-nav">
                  <button
                    className="calendar-nav-btn"
                    onClick={goToPrevMonth}
                    aria-label="Previous month"
                  >
                    <FontAwesomeIcon icon={faChevronLeft} />
                  </button>

                  <div className="calendar-nav-center">
                    <h2 className="calendar-month-title">
                      {activeStartDate.toLocaleString("default", {
                        month: "long",
                        year: "numeric",
                      })}
                    </h2>
                    <button className="today-btn" onClick={goToToday}>
                      Today
                    </button>
                  </div>

                  <button
                    className="calendar-nav-btn"
                    onClick={goToNextMonth}
                    aria-label="Next month"
                  >
                    <FontAwesomeIcon icon={faChevronRight} />
                  </button>
                </div>

                <Calendar
                  className="akar-calendar"
                  onChange={handleDateClick}
                  value={selectedDate}
                  activeStartDate={activeStartDate}
                  onActiveStartDateChange={({ activeStartDate: next }) =>
                    next && setActiveStartDate(next)
                  }
                  tileClassName={tileClassName}
                  tileContent={tileContent}
                  showNeighboringMonth={false}
                  // ✅ Force Sunday-first to match your header layout
                  calendarType="US"
                  locale="en-US"
                />

                {/* Legend */}
                <div className="calendar-legend">
                  <div className="legend-item">
                    <span className="legend-dot national-dot"></span>
                    <span>National Holiday</span>
                  </div>
                  <div className="legend-item">
                    <span className="legend-dot restricted-dot"></span>
                    <span>Restricted Holiday</span>
                  </div>
                  <div className="legend-item">
                    <span className="legend-dot fixed-dot"></span>
                    <span>Fixed Holiday</span>
                  </div>
                  <div className="legend-item">
                    <span className="legend-dot sunday-dot"></span>
                    <span>Sunday</span>
                  </div>
                </div>
              </div>

              {/* Selected Holiday */}
              {selectedHoliday && (
                <div className="card selected-holiday-card">
                  <div className="selected-holiday-header">
                    <div
                      className={`holiday-type-badge ${String(
                        selectedHoliday.holidayType || "",
                      )
                        .toLowerCase()
                        .replace(/\s+/g, "-")}`}
                    >
                      <FontAwesomeIcon
                        icon={getHolidayIcon(selectedHoliday.holidayType)}
                      />
                      {selectedHoliday.holidayType}
                    </div>

                    <span className="selected-holiday-date">
                      {parseDDMMYYYY(selectedHoliday.date)?.toLocaleDateString(
                        "en-GB",
                        {
                          weekday: "long",
                          day: "numeric",
                          month: "long",
                          year: "numeric",
                        },
                      )}
                    </span>
                  </div>

                  <h3 className="selected-holiday-name">
                    {selectedHoliday.holiday}
                  </h3>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="calendar-sidebar">
              {/* Upcoming Holidays */}
              <div className="card upcoming-holidays-card">
                <div className="card-header">
                  <h3 className="card-title">
                    <FontAwesomeIcon
                      icon={faCalendarAlt}
                      style={{
                        marginRight: "10px",
                        color: "var(--primary-color)",
                      }}
                    />
                    Upcoming Holidays
                  </h3>
                </div>

                <div className="card-body">
                  {getUpcomingHolidays().length > 0 ? (
                    <div className="upcoming-holidays-list">
                      {getUpcomingHolidays().map((holiday, index) => {
                        const d = holiday._d || parseDDMMYYYY(holiday.date);
                        return (
                          <div
                            key={index}
                            className="upcoming-holiday-item"
                            onClick={() => {
                              setSelectedDate(d);
                              setActiveStartDate(
                                new Date(d.getFullYear(), d.getMonth(), 1),
                              );
                              setSelectedHoliday(holiday);
                            }}
                          >
                            <div className="upcoming-holiday-date-box">
                              <span className="upcoming-day">{d.getDate()}</span>
                              <span className="upcoming-month">
                                {d.toLocaleString("default", { month: "short" })}
                              </span>
                            </div>

                            <div className="upcoming-holiday-info">
                              <span className="upcoming-holiday-name">
                                {holiday.holiday}
                              </span>
                              <span
                                className={`upcoming-holiday-type ${String(
                                  holiday.holidayType || "",
                                )
                                  .toLowerCase()
                                  .replace(/\s+/g, "-")}`}
                              >
                                {holiday.holidayType}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="no-upcoming-holidays">
                      <FontAwesomeIcon
                        icon={faCalendarAlt}
                        className="no-holidays-icon"
                      />
                      <p>No upcoming holidays</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Holiday Stats */}
              <div className="card holiday-stats-card">
                <div className="card-header">
                  <h3 className="card-title">Holiday Statistics</h3>
                </div>

                <div className="card-body">
                  <div className="stat-row">
                    <span className="stat-label">Total Holidays</span>
                    <span className="stat-value">{filteredHolidays.length}</span>
                  </div>

                  <div className="stat-row">
                    <span className="stat-label">National Holidays</span>
                    <span className="stat-value national-text">
                      {
                        filteredHolidays.filter((h) =>
                          (h.holidayType || "").toLowerCase().includes("national"),
                        ).length
                      }
                    </span>
                  </div>

                  <div className="stat-row">
                    <span className="stat-label">Restricted Holidays</span>
                    <span className="stat-value restricted-text">
                      {
                        filteredHolidays.filter((h) =>
                          (h.holidayType || "").toLowerCase().includes("restricted"),
                        ).length
                      }
                    </span>
                  </div>

                  <div className="stat-row">
                    <span className="stat-label">Fixed Holidays</span>
                    <span className="stat-value fixed-text">
                      {
                        filteredHolidays.filter((h) =>
                          (h.holidayType || "").toLowerCase().includes("fixed"),
                        ).length
                      }
                    </span>
                  </div>
                </div>
              </div>
              {/* end stats */}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default HolidayCalendar;
